import './fabricDefaults.ts';
import {
    CanvasTransform,
    CustomCanvas,
    CustomFabricObject,
    EventTypes,
    FabricEvent,
    DistributeDirection,
    FontOptions,
    FontSettings,
    ObjectAlignment,
    ObjectTypes,
    TextAlign,
    VideoElement
} from './types.ts';
import * as fabric from 'fabric';
import {v4 as uuidv4} from 'uuid';
import {
    AuthData,
    EntityDataModel,
    EntityManipulationMethods,
    MergeData,
    RemoveElement,
    UnknownObject
} from '../../types.ts';
import getChanges from '../../project_data/getChanges.ts';
import emptyGuid from '../../lib/emptyGuid.ts';
import {Player, PlayerEvent} from '../../project_data/useProjectData.ts';
import elementDataSorter from '../../project_data/elementDataSorter.ts';
import {
    findFabricObject,
    fireObjectModified,
    getObjectType,
    isClickable,
    modifyFabricObject,
    normalizeSerializedElement,
    restoreFabricObject,
    transformPoint,
    absoluteGeometry
} from './lib.ts';
import debounce from '../../lib/debounce.ts';
import {FileFormat} from '../../lib/FileFormats.ts';
import {nextFrames, TabViewController} from '../../lib/tabView.ts';
import React from 'react';
import {FileDataModel, MixModel} from '../../api/types.ts';
import {
    ProjectCard,
    ProjectCardOptions,
    ProjectCardTarget
} from './projectCard.ts';
import {NavigateFunction} from 'react-router-dom';
import {Sprite} from './sprite.ts';
import {FabricVideo} from './video.ts';
import {
    StickyNote,
    StickyNoteColors,
    StickyNoteDefaultFontFamily,
    StickyNoteTextAlign,
    StickyNoteTextSizes
} from './stickyNote.ts';
import {ResourceCard, ResourceCardTarget} from './resourceCard.ts';
import {LinkableImage} from './linkableImage.ts';
import {
    ElementJson,
    HistoryOp,
    WhiteboardHistory,
    getWhiteboardHistory
} from './history.ts';
import {
    GridBackground,
    GridSettings,
    loadGridSettings,
    saveGridSettings,
    snapToGrid
} from './canvasGrid.ts';
import isValidURL from '../../lib/isValidURL.ts';
import baseUri from '../../api/baseUri.ts';

interface StickyNoteDefaults {
    fill: string;
    fontSize: number;
    textAlign: StickyNoteTextAlign;
    fontFamily: string;
    fontWeight: 'normal' | 'bold';
    fontStyle: 'normal' | 'italic';
}

const stickyNoteDefaultsStorageKey = 'stickyNoteDefaults';

const loadStickyNoteDefaults = (): StickyNoteDefaults => {
    const fallback: StickyNoteDefaults = {
        fill: StickyNoteColors[0],
        fontSize: StickyNoteTextSizes.small,
        textAlign: 'center',
        fontFamily: StickyNoteDefaultFontFamily,
        fontWeight: 'normal',
        fontStyle: 'normal'
    };

    try {
        const stored = window.localStorage.getItem(
            stickyNoteDefaultsStorageKey
        );
        if (!stored) {
            return fallback;
        }
        const parsed = JSON.parse(stored);
        return {
            fill: typeof parsed.fill === 'string' ? parsed.fill : fallback.fill,
            fontSize:
                typeof parsed.fontSize === 'number'
                    ? parsed.fontSize
                    : fallback.fontSize,
            textAlign: ['left', 'center', 'right'].includes(parsed.textAlign)
                ? parsed.textAlign
                : fallback.textAlign,
            fontFamily:
                typeof parsed.fontFamily === 'string'
                    ? parsed.fontFamily
                    : fallback.fontFamily,
            fontWeight: ['normal', 'bold'].includes(parsed.fontWeight)
                ? parsed.fontWeight
                : fallback.fontWeight,
            fontStyle: ['normal', 'italic'].includes(parsed.fontStyle)
                ? parsed.fontStyle
                : fallback.fontStyle
        };
    } catch {
        return fallback;
    }
};

// How far a duplicate sits from its original, so both show.
const DUPLICATE_OFFSET = 20;

class CanvasController {
    initialized: boolean;
    public canvas: CustomCanvas | null;
    public pencilWidth: number = 5;
    public shadowWidth: number = 0;
    public shadowOffset: number = 0;
    public drawingMode: string = 'Pencil';
    public showPencilToolbar: boolean = false;

    public videoElements: VideoElement[] = [];
    public currentColor: string = '#000000';
    public fontSettings: FontSettings = {
        bold: false,
        italic: false,
        font: 'Arial'
    };

    // The grid is a per-browser guide (shown, snapped to, spacing), never part of the board.
    public grid: GridSettings = loadGridSettings();
    private gridBackground: GridBackground | null = null;

    // Undo and redo of this person's own actions on this board (history.ts).
    private history: WhiteboardHistory;
    private lastSeededData: EntityDataModel | null = null;

    public get canUndo(): boolean {
        return this.history.canUndo;
    }

    public get canRedo(): boolean {
        return this.history.canRedo;
    }

    // Last chosen sticky note styling (colour, text size, alignment, font),
    // applied to newly created notes and persisted per browser.
    public stickyNoteDefaults: StickyNoteDefaults = loadStickyNoteDefaults();

    public players: Record<string, Player> = {};
    public canvasTransform: CanvasTransform = {
        scale: 1,
        translationX: 0,
        translationY: 1
    };
    public isReadOnly: boolean = false;

    public get activeObjectType(): string | null {
        if (!this.canvas) {
            return null;
        }

        const activeObject = this.canvas.getActiveObject();
        return activeObject ? getObjectType(activeObject) : null;
    }

    public get activeObjectMeta(): string | null | undefined {
        if (!this.canvas) {
            return null;
        }
        const activeObject = this.canvas.getActiveObject();
        const meta = (activeObject as CustomFabricObject).meta;
        return meta;
    }

    public activeObjectFontSettings(): FontSettings | null {
        if (!this.canvas) {
            return null;
        }

        const activeObject = this.canvas.getActiveObject();

        if (activeObject && getObjectType(activeObject) == ObjectTypes.Text) {
            const textObject = activeObject as fabric.IText;
            return {
                bold: textObject.fontWeight === 'bold',
                italic: textObject.fontStyle === 'italic',
                font: textObject.fontFamily,
                fontSize: textObject.fontSize,
                align: ['left', 'center', 'right'].includes(
                    textObject.textAlign
                )
                    ? (textObject.textAlign as TextAlign)
                    : 'left'
            };
        }

        return null;
    }

    // Whether someone is typing into a text or note, when keys belong to the text and not to the board.
    public get isEditingText(): boolean {
        return (
            this.editingTextElementId !== null ||
            this.canvas?.getObjects().some(obj => {
                const text = obj as fabric.IText;
                return text.isEditing === true;
            }) === true
        );
    }

    private _movingCanvas: boolean = false;

    public get movingCanvas(): boolean {
        return this._movingCanvas;
    }

    public get isDrawing(): boolean {
        return (
            this.canvas !== null &&
            !this.isReadOnly &&
            this.canvas.isDrawingMode == true
        );
    }

    public get isSelectActive(): boolean {
        return (
            this.canvas !== null &&
            !this.isReadOnly &&
            !this.canvas.isDrawingMode &&
            !this._movingCanvas
        );
    }

    private editingTextElementId: string | null = null;
    private initialData: EntityDataModel | null;
    private initialDataObjectsRestored: boolean;
    private authData: AuthData;
    private eventHandlers: Record<string, (e: FabricEvent) => void>;
    private getCurrentProjectData: () => EntityDataModel;
    private manipulation: EntityManipulationMethods;
    private playerEvent: (player: PlayerEvent) => void;
    private playerTimersRef: React.MutableRefObject<{[p: string]: number}>;
    private storageUri: string;
    private openAssetSelectAndUploadModal: (
        fileFormat: FileFormat,
        assetExplanation: string,
        onFileSelected: (file: FileDataModel | null) => void
    ) => void;
    private stateHasChanged: () => void;
    private navigate: NavigateFunction;

    constructor(
        isReadOnly: boolean,
        authData: AuthData,
        playerTimersRef: React.MutableRefObject<{[p: string]: number}>,
        storageUri: string,
        openAssetSelectAndUploadModal: (
            fileFormat: FileFormat,
            assetExplanation: string,
            onFileSelected: (file: FileDataModel | null) => void
        ) => void,
        stateHasChanged: () => void,
        navigate: NavigateFunction,
        // Names the board, so its undo history outlives this controller (a tab switch makes a new one).
        historyKey: string
    ) {
        this.initialized = false;
        this.canvas = null;
        this.initialData = null;
        this.initialDataObjectsRestored = false;
        this.isReadOnly = isReadOnly;
        this.authData = authData;
        this.getCurrentProjectData = () => ({});
        this.manipulation = {
            removeElement: () => {},
            mergeData: () => {}
        };
        this.playerEvent = () => {};
        this.playerTimersRef = playerTimersRef;
        this.storageUri = storageUri;
        this.openAssetSelectAndUploadModal = openAssetSelectAndUploadModal;
        this.stateHasChanged = stateHasChanged;
        this.navigate = navigate;
        this.history = getWhiteboardHistory(historyKey, () =>
            this.stateHasChanged()
        );

        this.eventHandlers = {
            [EventTypes.ObjectMoving]: this.handleObjectMoving,
            [EventTypes.ObjectScaling]: this.handleObjectScaling,
            [EventTypes.ObjectRotating]: this.handleObjectRotating,
            [EventTypes.ObjectSkewing]: this.handleObjectSkewing,
            [EventTypes.ObjectAdded]: this.handleObjectAdded,
            [EventTypes.ObjectRemoved]: this.handleObjectRemoved,
            [EventTypes.ObjectModified]: this.handleObjectModified,
            [EventTypes.PathCreated]: this.handlePathCreated,
            [EventTypes.MouseDown]: this.handleMouseDown,
            [EventTypes.MouseDoubleClick]: this.handleMouseDoubleClick,
            [EventTypes.MouseMove]: this.handleMouseMove,
            [EventTypes.MouseOut]: this.handleMouseOut,
            [EventTypes.MouseUp]: this.handleMouseUp,
            [EventTypes.SelectionCreated]: this.handleSelectionCreated,
            [EventTypes.SelectionUpdated]: this.handleSelectionUpdated,
            [EventTypes.SelectionCleared]: this.handleSelectionCleared,
            [EventTypes.TextEditingEntered]: this.handleTextEditingEntered,
            [EventTypes.TextEditingExited]: this.handleTextEditingExited
        };
    }

    onDelete() {
        if (!this.canvas) {
            return;
        }

        this.removeSelected();
    }

    getObjects = () => {
        return this.canvas?.getObjects() ?? [];
    };

    getSelections = () => {
        if (!this.canvas) {
            return [];
        }

        const currentSelection = this.canvas.getActiveObject();
        if (currentSelection) {
            return [currentSelection];
        } else {
            const currentSelections = this.canvas.getActiveObjects();
            return currentSelections;
        }
    };

    toggleIsDrawing = () => {
        if (this.isDrawing) {
            this.disableFreehandDrawing();
            this.showPencilToolbar = false;
        } else {
            this.enableFreehandDrawing();
            this.showPencilToolbar = true;
        }

        this.stateHasChanged();
    };

    selectClicked = () => {
        this.disableFreehandDrawing();
        this.setMovingCanvas(false);
        this.stateHasChanged();
    };

    private onIsReadOnlyChange = (isReadOnly: boolean) => {
        this.isReadOnly = isReadOnly;
        if (!this.canvas) {
            return;
        }

        const canvas = this.canvas;
        if (isReadOnly) {
            canvas.selection = false;
            canvas.forEachObject(object => {
                object.selectable = false;
                this.applyReadOnlyClickability(object);
            });
            this.setMovingCanvas(true);
        } else {
            canvas.selection = true;
            canvas.forEachObject(object => {
                object.selectable = true;
                object.evented = true;
                if (isClickable(object)) {
                    object.hoverCursor = 'move';
                }
            });
        }

        canvas.requestRenderAll();
        this.stateHasChanged();
    };

    // In read-only mode only clickable objects receive events at all.
    private applyReadOnlyClickability(object: fabric.FabricObject) {
        if (isClickable(object)) {
            object.evented = true;
            object.hoverCursor = 'pointer';
        } else {
            object.evented = false;
        }
    }

    setIsReadOnly = (isReadOnly: boolean) => {
        this.onIsReadOnlyChange(isReadOnly);
    };

    toggleReadOnlyMode = () => {
        const isReadOnly = this.isReadOnly;
        this.onIsReadOnlyChange(!isReadOnly);
    };

    toggleMovingCanvas = () => {
        this.setMovingCanvas(!this._movingCanvas);
    };

    private setMovingCanvas(movingCanvas: boolean) {
        const canvas = this.canvas!;

        /*
         * Always detach first so toggling (or re-entering read-only mode,
         * which calls this from both init and setIsReadOnly) never stacks
         * duplicate handlers.
         */
        this.detachCanvasNavigation();

        if (movingCanvas) {
            this.zoomCanvas();
            this.dragCanvas();
            this.pinchCanvas();
            canvas.selection = false;
            canvas.forEachObject(object => {
                object.selectable = false;
                if (!isClickable(object)) {
                    object.evented = false;
                }
            });
            this.disableFreehandDrawing();
        } else {
            canvas.selection = true;
            canvas.forEachObject(object => {
                object.selectable = true;
                object.evented = true;
            });
            canvas.defaultCursor = 'default';
        }

        this._movingCanvas = movingCanvas;

        this.stateHasChanged();
    }

    private readonly minZoom = 0.01;
    private readonly maxZoom = 20;

    private clampZoom = (zoom: number) =>
        Math.min(this.maxZoom, Math.max(this.minZoom, zoom));

    private syncCanvasTransform = () => {
        const vpt = this.canvas?.viewportTransform;
        if (vpt) {
            this.canvasTransform = {
                scale: vpt[0],
                translationX: vpt[4],
                translationY: vpt[5]
            };
        }
    };

    /*
     * The view, for links that open the whiteboard at a spot and for the
     * render page (lib/tabView.ts). Everything is in canvas units: x and y
     * are the canvas point at the centre of the view.
     */
    viewController: TabViewController = {
        ready: async () => {
            // The data arrives, then pictures and cards load one by one; drawn means nothing has been added for a
            // moment.
            const started = Date.now();
            while (
                !(this.initialized && this.initialDataObjectsRestored) &&
                Date.now() - started < 20_000
            ) {
                await new Promise(resolve => window.setTimeout(resolve, 100));
            }

            let count = -1;
            let stableSince = Date.now();
            while (
                Date.now() - stableSince < 800 &&
                Date.now() - started < 20_000
            ) {
                const now = this.canvas?.getObjects().length ?? 0;
                if (now !== count) {
                    count = now;
                    stableSince = Date.now();
                }
                await new Promise(resolve => window.setTimeout(resolve, 100));
            }

            this.canvas?.renderAll();
            await nextFrames();
        },
        apply: async view => {
            const canvas = this.canvas;
            if (!canvas) {
                return {};
            }

            const width = canvas.getWidth();
            const height = canvas.getHeight();
            const bounds = this.contentBounds();
            const current = canvas.viewportTransform;
            let zoom = current[0];
            let centre = {
                x: (width / 2 - current[4]) / zoom,
                y: (height / 2 - current[5]) / zoom
            };

            const area = view?.region
                ? {
                      x: view.region[0],
                      y: view.region[1],
                      width: view.region[2],
                      height: view.region[3]
                  }
                : view?.fit
                ? bounds
                : null;

            if (area && area.width > 0 && area.height > 0) {
                // A little margin, so nothing sits right on the edge.
                const margin = view?.region ? 1 : 1.08;
                zoom = this.clampZoom(
                    Math.min(
                        width / (area.width * margin),
                        height / (area.height * margin)
                    )
                );
                centre = {
                    x: area.x + area.width / 2,
                    y: area.y + area.height / 2
                };
            }

            if (view?.zoom !== undefined) {
                zoom = this.clampZoom(view.zoom);
            }
            if (view?.x !== undefined) {
                centre = {...centre, x: view.x};
            }
            if (view?.y !== undefined) {
                centre = {...centre, y: view.y};
            }

            canvas.setViewportTransform([
                zoom,
                0,
                0,
                zoom,
                width / 2 - centre.x * zoom,
                height / 2 - centre.y * zoom
            ]);
            this.syncCanvasTransform();
            canvas.renderAll();
            await nextFrames();

            const round = (value: number) => Math.round(value * 10) / 10;
            return {
                type: 'whiteboard',
                view: {
                    x: round(centre.x),
                    y: round(centre.y),
                    zoom: Math.round(zoom * 1000) / 1000
                },
                visible: {
                    x: round(centre.x - width / 2 / zoom),
                    y: round(centre.y - height / 2 / zoom),
                    width: round(width / zoom),
                    height: round(height / zoom)
                },
                content: bounds && {
                    x: round(bounds.x),
                    y: round(bounds.y),
                    width: round(bounds.width),
                    height: round(bounds.height)
                },
                objects: canvas.getObjects().length
            };
        }
    };

    // The area every object covers, in canvas units; null on an empty whiteboard.
    private contentBounds = () => {
        const objects = this.canvas?.getObjects() ?? [];
        if (objects.length === 0) {
            return null;
        }

        let left = Infinity;
        let top = Infinity;
        let right = -Infinity;
        let bottom = -Infinity;
        for (const object of objects) {
            const box = object.getBoundingRect();
            left = Math.min(left, box.left);
            top = Math.min(top, box.top);
            right = Math.max(right, box.left + box.width);
            bottom = Math.max(bottom, box.top + box.height);
        }

        return {x: left, y: top, width: right - left, height: bottom - top};
    };

    /*
     * Handlers registered by zoomCanvas / dragCanvas / pinchCanvas, kept so
     * they can be removed again. Fabric fires its mouse:* events for touch
     * input too (with the raw TouchEvent as opt.e), so the drag handlers
     * work from opt.viewportPoint rather than clientX/clientY, which a
     * TouchEvent does not have. Pinch-to-zoom has no Fabric equivalent, so
     * it listens to the native touch events on the upper canvas.
     */
    private navigationHandlers: {
        wheel?: (opt: fabric.TPointerEventInfo<WheelEvent>) => void;
        down?: (opt: fabric.TPointerEventInfo) => void;
        move?: (opt: fabric.TPointerEventInfo) => void;
        up?: () => void;
        touchStart?: (e: TouchEvent) => void;
        touchMove?: (e: TouchEvent) => void;
        touchEnd?: (e: TouchEvent) => void;
        blur?: () => void;
    } = {};

    private pinch: {
        startDistance: number;
        startZoom: number;
        lastMidpoint: fabric.Point;
    } | null = null;

    private detachCanvasNavigation = () => {
        const canvas = this.canvas;
        const handlers = this.navigationHandlers;
        if (!canvas) {
            this.navigationHandlers = {};
            this.pinch = null;
            return;
        }

        handlers.wheel && canvas.off('mouse:wheel', handlers.wheel);
        handlers.down && canvas.off('mouse:down', handlers.down);
        handlers.move && canvas.off('mouse:move', handlers.move);
        handlers.up && canvas.off('mouse:up', handlers.up);

        const upper = canvas.upperCanvasEl;
        if (upper) {
            handlers.touchStart &&
                upper.removeEventListener('touchstart', handlers.touchStart);
            handlers.touchMove &&
                upper.removeEventListener('touchmove', handlers.touchMove);
            handlers.touchEnd &&
                upper.removeEventListener('touchend', handlers.touchEnd);
            handlers.touchEnd &&
                upper.removeEventListener('touchcancel', handlers.touchEnd);
        }

        handlers.blur && window.removeEventListener('blur', handlers.blur);

        canvas.isDragging = false;
        canvas.lastPosX = undefined;
        canvas.lastPosY = undefined;
        this.navigationHandlers = {};
        this.pinch = null;
    };

    private zoomCanvas = () => {
        const canvas = this.canvas!;
        const wheel = (opt: fabric.TPointerEventInfo<WheelEvent>) => {
            const delta = opt.e.deltaY;
            const zoom = this.clampZoom(canvas.getZoom() * 0.999 ** delta);

            canvas.setZoom(zoom);
            opt.e.preventDefault();
            opt.e.stopPropagation();
            this.syncCanvasTransform();
        };

        this.navigationHandlers.wheel = wheel;
        canvas.on('mouse:wheel', wheel);
    };

    private dragCanvas = () => {
        const canvas = this.canvas!;
        canvas.defaultCursor = 'grab';

        const down = (opt: fabric.TPointerEventInfo) => {
            if (this.pinch) {
                return;
            }

            canvas.isDragging = true;
            canvas.selection = false;
            canvas.lastPosX = opt.viewportPoint.x;
            canvas.lastPosY = opt.viewportPoint.y;
        };

        const endDrag = () => {
            if (canvas.isDragging && canvas.viewportTransform) {
                canvas.setViewportTransform(canvas.viewportTransform);
            }

            canvas.isDragging = false;
            canvas.lastPosX = undefined;
            canvas.lastPosY = undefined;
            canvas.selection = !this.isReadOnly && !this._movingCanvas;
        };

        const move = (opt: fabric.TPointerEventInfo) => {
            if (!canvas.isDragging || this.pinch || !canvas.viewportTransform) {
                return;
            }

            /*
             * A mouse move with no button held means the mouse:up was lost,
             * typically because mouse:down opened a resource card's link in
             * a new tab and the release happened there. Without this the
             * board would keep following the pointer until a refresh.
             */
            const e = opt.e;
            if (e instanceof MouseEvent && e.buttons === 0) {
                endDrag();
                return;
            }

            const {x, y} = opt.viewportPoint;

            /*
             * lastPos is cleared when a pinch ends with one finger still
             * down, so the remaining finger resumes panning from where it
             * is instead of jumping by the distance it travelled while
             * pinching.
             */
            if (
                canvas.lastPosX !== undefined &&
                canvas.lastPosY !== undefined
            ) {
                const vpt = canvas.viewportTransform;
                vpt[4] += x - canvas.lastPosX;
                vpt[5] += y - canvas.lastPosY;
                canvas.requestRenderAll();
                this.syncCanvasTransform();
            }

            canvas.lastPosX = x;
            canvas.lastPosY = y;
        };

        const up = () => endDrag();

        // Losing focus mid-gesture (new tab, alt-tab) also ends it.
        const blur = () => {
            this.pinch = null;
            endDrag();
        };

        this.navigationHandlers.down = down;
        this.navigationHandlers.move = move;
        this.navigationHandlers.up = up;
        this.navigationHandlers.blur = blur;
        canvas.on('mouse:down', down);
        canvas.on('mouse:move', move);
        canvas.on('mouse:up', up);
        window.addEventListener('blur', blur);
    };

    /*
     * A touch's position in viewport (canvas pixel) coordinates, matching
     * what Fabric's getViewportPoint returns for a single-touch event.
     */
    private touchToViewportPoint = (touch: Touch): fabric.Point => {
        const canvas = this.canvas!;
        const upper = canvas.upperCanvasEl;
        const bounds = upper.getBoundingClientRect();
        const cssScaleX = bounds.width ? upper.width / bounds.width : 1;
        const cssScaleY = bounds.height ? upper.height / bounds.height : 1;
        const retina = canvas.getRetinaScaling();
        return new fabric.Point(
            ((touch.clientX - bounds.left) * cssScaleX) / retina,
            ((touch.clientY - bounds.top) * cssScaleY) / retina
        );
    };

    private pinchCanvas = () => {
        const canvas = this.canvas!;
        const upper = canvas.upperCanvasEl;
        if (!upper) {
            return;
        }

        const pinchGeometry = (touches: TouchList) => {
            const a = this.touchToViewportPoint(touches[0]);
            const b = this.touchToViewportPoint(touches[1]);
            return {
                distance: Math.hypot(b.x - a.x, b.y - a.y),
                midpoint: new fabric.Point((a.x + b.x) / 2, (a.y + b.y) / 2)
            };
        };

        const touchStart = (e: TouchEvent) => {
            if (e.touches.length !== 2) {
                return;
            }

            const {distance, midpoint} = pinchGeometry(e.touches);
            this.pinch = {
                startDistance: distance || 1,
                startZoom: canvas.getZoom(),
                lastMidpoint: midpoint
            };
            canvas.isDragging = false;
            e.preventDefault();
        };

        const touchMove = (e: TouchEvent) => {
            const pinch = this.pinch;
            if (!pinch || e.touches.length < 2 || !canvas.viewportTransform) {
                return;
            }

            e.preventDefault();
            const {distance, midpoint} = pinchGeometry(e.touches);
            const zoom = this.clampZoom(
                pinch.startZoom * (distance / pinch.startDistance)
            );

            // Zoom about the fingers' midpoint, then follow it as it moves.
            canvas.zoomToPoint(midpoint, zoom);
            const vpt = canvas.viewportTransform;
            vpt[4] += midpoint.x - pinch.lastMidpoint.x;
            vpt[5] += midpoint.y - pinch.lastMidpoint.y;
            canvas.setViewportTransform(vpt);
            pinch.lastMidpoint = midpoint;
            canvas.requestRenderAll();
            this.syncCanvasTransform();
        };

        const touchEnd = (e: TouchEvent) => {
            if (!this.pinch || e.touches.length >= 2) {
                return;
            }

            this.pinch = null;
            if (e.touches.length === 1) {
                // Hand over to single-finger panning from the finger's
                // current position (see the move handler in dragCanvas).
                canvas.isDragging = true;
                canvas.lastPosX = undefined;
                canvas.lastPosY = undefined;
            }
        };

        this.navigationHandlers.touchStart = touchStart;
        this.navigationHandlers.touchMove = touchMove;
        this.navigationHandlers.touchEnd = touchEnd;
        upper.addEventListener('touchstart', touchStart, {passive: false});
        upper.addEventListener('touchmove', touchMove, {passive: false});
        upper.addEventListener('touchend', touchEnd);
        upper.addEventListener('touchcancel', touchEnd);
    };

    handleBackgroundColorChange = () => {
        if (this.canvas) {
            // The board's settings element is stored with type 'settings'. Finding it
            // (in the history's mirror, which is never a render behind) is what stops
            // each background change adding another one.
            const settings = this.history.findSettings();
            const settingsElementId = settings ? settings[0] : uuidv4();

            const newState: UnknownObject = settings
                ? {
                      ...settings[1],
                      elementId: settings[1].elementId ?? settingsElementId,
                      canvasBackgroundColor: this.currentColor
                  }
                : {
                      type: 'settings',
                      version: '',
                      elementId: settingsElementId,
                      canvasBackgroundColor: this.currentColor
                  };

            this.history.recordSettings(settingsElementId, newState);

            // Whole and as added, new or not: settings aren't a canvas object,
            // so others' boards only apply an add, and that needs the type.
            this.mergeData(settingsElementId, newState, EventTypes.ObjectAdded);

            this.canvas.backgroundColor = this.currentColor;
            this.canvas.renderAll();
        }
    };

    handleLineWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.pencilWidth = parseFloat(e.target.value);
        this.enableFreehandDrawing();
    };

    handleShadowWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.shadowWidth = parseFloat(e.target.value);
        this.enableFreehandDrawing();
    };

    handleShadowOffsetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.shadowOffset = parseFloat(e.target.value);
        this.enableFreehandDrawing();
    };

    onDrawingModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.drawingMode = e.target.value;
        this.enableFreehandDrawing();
    };

    getDiamondPatternSource = () => {
        const diamondWidth = 10;
        const diamondDistance = 5;
        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = patternCanvas.height =
            diamondWidth + diamondDistance;
        const ctx = patternCanvas.getContext('2d');
        if (ctx) {
            const rect = new fabric.Rect({
                width: diamondWidth,
                height: diamondWidth,
                angle: 45,
                fill: this.currentColor,
                left: diamondWidth / 2,
                top: diamondWidth / 2
            });
            rect.render(ctx);
        }
        return patternCanvas;
    };

    getHLinePatternSource = () => {
        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = patternCanvas.height = 10;
        const ctx = patternCanvas.getContext('2d');

        if (ctx) {
            ctx.lineWidth = 5;
            ctx.beginPath();
            ctx.moveTo(0, 5);
            ctx.lineTo(10, 5);
            ctx.closePath();
            ctx.stroke();
        }

        return patternCanvas;
    };

    getVLinePatternSource = () => {
        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = patternCanvas.height = 10;
        const ctx = patternCanvas.getContext('2d');

        if (ctx) {
            ctx.lineWidth = 5;
            ctx.beginPath();
            ctx.moveTo(5, 0);
            ctx.lineTo(5, 10);
            ctx.closePath();
            ctx.stroke();
        }

        return patternCanvas;
    };

    getSquarePatternSource = () => {
        const squareWidth = 10,
            squareDistance = 2;

        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = patternCanvas.height =
            squareWidth + squareDistance;
        const ctx = patternCanvas.getContext('2d');

        if (ctx) {
            ctx.fillRect(0, 0, squareWidth, squareWidth);
        }

        return patternCanvas;
    };

    handleRecenterCanvas = () => {
        const canvas = this.canvas!;
        canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        canvas.requestRenderAll();
    };

    enableFreehandDrawing = () => {
        let drawingBrush: fabric.BaseBrush;
        if (this.drawingMode === 'Diamond') {
            const patternBrush = new fabric.PatternBrush(this.canvas!);
            patternBrush.getPatternSrc = this.getDiamondPatternSource;
            patternBrush.source = patternBrush.getPatternSrc.call(patternBrush);
            drawingBrush = patternBrush;
        } else if (this.drawingMode === 'VLine') {
            const patternBrush = new fabric.PatternBrush(this.canvas!);
            patternBrush.getPatternSrc = this.getVLinePatternSource;
            patternBrush.source = patternBrush.getPatternSrc.call(patternBrush);
            drawingBrush = patternBrush;
        } else if (this.drawingMode === 'HLine') {
            const patternBrush = new fabric.PatternBrush(this.canvas!);
            patternBrush.getPatternSrc = this.getHLinePatternSource;
            patternBrush.source = patternBrush.getPatternSrc.call(patternBrush);
            drawingBrush = patternBrush;
        } else if (this.drawingMode === 'Square') {
            const patternBrush = new fabric.PatternBrush(this.canvas!);
            patternBrush.getPatternSrc = this.getSquarePatternSource;
            patternBrush.source = patternBrush.getPatternSrc.call(patternBrush);
            drawingBrush = patternBrush;
        } else if (this.drawingMode === 'Spray') {
            drawingBrush = new fabric.SprayBrush(this.canvas!);
        } else if (this.drawingMode === 'Circle') {
            drawingBrush = new fabric.CircleBrush(this.canvas!);
        } else {
            drawingBrush = new fabric.PencilBrush(this.canvas!);
        }

        drawingBrush.color = this.currentColor;
        drawingBrush.width = this.pencilWidth;
        drawingBrush.shadow = new fabric.Shadow({
            blur: this.shadowWidth,
            offsetX: this.shadowOffset,
            offsetY: this.shadowOffset,
            affectStroke: true,
            color: '#000'
        });

        this.canvas!.freeDrawingBrush = drawingBrush;

        this.canvas!.isDrawingMode = true;
        this.setMovingCanvas(false);
    };

    disableFreehandDrawing = () => {
        this.canvas!.isDrawingMode = false;
    };

    removeSelected = () => {
        const canvas = this.canvas!;
        let isEditingText = false;
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
            canvas.getActiveObjects().forEach(obj => {
                const customObject = obj as CustomFabricObject;
                if (obj instanceof FabricVideo && customObject.videoElement) {
                    this.disposeObject(obj);
                    canvas.remove(obj);
                } else if (
                    (getObjectType(obj) === ObjectTypes.Text &&
                        customObject.elementId === this.editingTextElementId) ||
                    (obj instanceof fabric.IText && obj.isEditing)
                ) {
                    // Covers plain i-texts being edited and the nested
                    // textbox of a sticky note (Backspace while typing must
                    // not delete the object).
                    isEditingText = true;
                } else {
                    canvas.remove(obj);
                }
            });

            if (!isEditingText) {
                canvas.discardActiveObject();
                canvas.requestRenderAll();
                canvas.remove(activeObject);
                this.stateHasChanged();
            }
        }
    };

    /*
     * Creates a project card. The picker has already handed us everything the
     * card shows, so nothing is fetched here; layout, image loading and the
     * placeholders all live in ProjectCard.
     */
    addProjectLink = (mix: MixModel, target?: ProjectCardTarget) => {
        const canvas = this.canvas!;

        this.disableFreehandDrawing();
        const canvasTransform = this.canvasTransform;

        const newPositionX = canvas.width
            ? canvas.width / 2 / canvasTransform.scale -
              110 -
              canvasTransform.translationX / canvasTransform.scale
            : 50;
        const newPositionY = canvas.height
            ? canvas.height / 2 / canvasTransform.scale -
              100 -
              canvasTransform.translationY / canvasTransform.scale
            : 50;

        const card = this.createProjectCard(mix, {
            left: newPositionX,
            top: newPositionY,
            ...(target?.tab ? target : {})
        });

        canvas.add(card);
        canvas.setActiveObject(card);
        void card.loadImages();

        this.setMovingCanvas(false);
        this.stateHasChanged();
    };

    private createProjectCard = (
        mix: MixModel,
        geometry: ProjectCardOptions
    ): ProjectCard => {
        const card = new ProjectCard({
            ...geometry,
            // Matches what projectUrl builds: the custom url when there is one.
            projectRef: mix.customUrl || mix.id || '',
            mixId: mix.id ?? '',
            name: mix.name,
            nickname: mix.nickname,
            thumbnail: mix.thumbnail,
            userThumbnail: mix.userThumbnail
        });

        (card as unknown as CustomFabricObject).elementId = uuidv4();

        return card;
    };

    get activeProjectCard(): ProjectCard | null {
        const activeObject = this.canvas?.getActiveObject();
        return activeObject instanceof ProjectCard ? activeObject : null;
    }

    // Which tab the selected card opens, and whether full screen.
    setProjectCardTarget = (target: ProjectCardTarget) => {
        const card = this.activeProjectCard;
        if (!card) {
            return;
        }

        card.setTarget(target);
        this.canvas!.requestRenderAll();
        fireObjectModified(this.canvas!, card);
        this.stateHasChanged();
    };

    /*
     * Identifies a card built by the old addProjectLink: a plain group carrying
     * the fixed-group marker and an in-app /project/ route. Detecting them is
     * what lets existing boards be left completely alone; a card is upgraded
     * only when someone selects it and asks, and boards heal as people work.
     *
     * Returns the project reference from the route, which is an id or a custom
     * url, so the caller can look the project up before replacing the card.
     */
    get selectedLegacyProjectCardRef(): string | null {
        const activeObject = this.canvas?.getActiveObject() as
            | CustomFabricObject
            | undefined;

        if (
            !activeObject ||
            activeObject instanceof ProjectCard ||
            getObjectType(activeObject) !== ObjectTypes.Group ||
            activeObject.meta !== 'fixed-group'
        ) {
            return null;
        }

        const prefix = '/project/';
        const nav = activeObject.nav ?? '';

        return nav.startsWith(prefix) ? nav.substring(prefix.length) : null;
    }

    /*
     * Swaps a legacy card for a current one in place, keeping its position,
     * rotation and scale. The removal and the insert each persist through the
     * normal element events, so the board ends up with one card as before.
     */
    replaceLegacyProjectCard = (mix: MixModel) => {
        const canvas = this.canvas!;
        const oldCard = canvas.getActiveObject() as
            | CustomFabricObject
            | undefined;

        if (!oldCard || this.selectedLegacyProjectCardRef === null) {
            return;
        }

        const card = this.createProjectCard(mix, {
            left: oldCard.left,
            top: oldCard.top,
            scaleX: oldCard.scaleX,
            scaleY: oldCard.scaleY,
            angle: oldCard.angle
        });

        canvas.discardActiveObject();
        canvas.remove(oldCard);
        canvas.add(card);
        canvas.setActiveObject(card);
        void card.loadImages();
        canvas.requestRenderAll();

        this.stateHasChanged();
    };

    addRectangle = () => {
        this.disableFreehandDrawing();
        const canvasTransform = this.canvasTransform;

        const newRectanglePositionX = this.canvas!.width
            ? this.canvas!.width / 2 / canvasTransform.scale -
              30 -
              canvasTransform.translationX / canvasTransform.scale
            : 50;
        const newRectanglePositionY = this.canvas!.height
            ? this.canvas!.height / 2 / canvasTransform.scale -
              35 -
              canvasTransform.translationY / canvasTransform.scale
            : 50;
        const elementId = uuidv4();
        const rect = new fabric.Rect({
            left: newRectanglePositionX,
            top: newRectanglePositionY,
            fill: this.currentColor,
            width: 60,
            height: 70,
            angle: 0,
            transparentCorners: false
        }) as CustomFabricObject;
        rect.elementId = elementId;

        this.canvas!.add(rect);
        this.canvas!.setActiveObject(rect);

        this.setMovingCanvas(false);
    };

    addCircle = () => {
        this.disableFreehandDrawing();
        const canvasTransform = this.canvasTransform;

        const newCirclePositionX = this.canvas!.width
            ? this.canvas!.width / 2 / canvasTransform.scale -
              30 -
              canvasTransform.translationX / canvasTransform.scale
            : 50;
        const newCirclePositionY = this.canvas!.height
            ? this.canvas!.height / 2 / canvasTransform.scale -
              30 -
              canvasTransform.translationY / canvasTransform.scale
            : 50;
        const elementId = uuidv4();
        const circle = new fabric.Circle({
            left: newCirclePositionX,
            top: newCirclePositionY,
            fill: this.currentColor,
            radius: 30,
            transparentCorners: false
        }) as unknown as CustomFabricObject;
        circle.elementId = elementId;

        this.canvas!.add(circle);
        this.canvas!.setActiveObject(circle);
        this.setMovingCanvas(false);
    };

    public get activeStickyNote(): StickyNote | null {
        const activeObject = this.canvas?.getActiveObject();
        return activeObject instanceof StickyNote ? activeObject : null;
    }

    private rememberStickyNoteDefaults = (
        changes: Partial<StickyNoteDefaults>
    ) => {
        this.stickyNoteDefaults = {...this.stickyNoteDefaults, ...changes};
        try {
            window.localStorage.setItem(
                stickyNoteDefaultsStorageKey,
                JSON.stringify(this.stickyNoteDefaults)
            );
        } catch {
            // Remembering across sessions is best-effort only.
        }
    };

    setStickyNoteColor = (color: string) => {
        this.rememberStickyNoteDefaults({fill: color});

        const note = this.activeStickyNote;
        if (note) {
            note.setColor(color);
            this.canvas!.requestRenderAll();
            fireObjectModified(this.canvas!, note);
        }

        this.stateHasChanged();
    };

    setStickyNoteFontSize = (fontSize: number) => {
        this.rememberStickyNoteDefaults({fontSize});

        const note = this.activeStickyNote;
        if (note) {
            note.setFontSize(fontSize);
            this.canvas!.requestRenderAll();
            fireObjectModified(this.canvas!, note);
        }

        this.stateHasChanged();
    };

    setStickyNoteTextAlign = (textAlign: StickyNoteTextAlign) => {
        this.rememberStickyNoteDefaults({textAlign});

        const note = this.activeStickyNote;
        if (note) {
            note.setTextAlign(textAlign);
            this.canvas!.requestRenderAll();
            fireObjectModified(this.canvas!, note);
        }

        this.stateHasChanged();
    };

    setStickyNoteFontFamily = (fontFamily: string) => {
        this.rememberStickyNoteDefaults({fontFamily});

        const note = this.activeStickyNote;
        if (note) {
            note.setFontFamily(fontFamily);
            this.canvas!.requestRenderAll();
            fireObjectModified(this.canvas!, note);
        }

        this.stateHasChanged();
    };

    toggleStickyNoteBold = () => {
        const note = this.activeStickyNote;
        const bold = !(
            note?.bold ?? this.stickyNoteDefaults.fontWeight === 'bold'
        );
        this.rememberStickyNoteDefaults({
            fontWeight: bold ? 'bold' : 'normal'
        });

        if (note) {
            note.setBold(bold);
            this.canvas!.requestRenderAll();
            fireObjectModified(this.canvas!, note);
        }

        this.stateHasChanged();
    };

    toggleStickyNoteItalic = () => {
        const note = this.activeStickyNote;
        const italic = !(
            note?.italic ?? this.stickyNoteDefaults.fontStyle === 'italic'
        );
        this.rememberStickyNoteDefaults({
            fontStyle: italic ? 'italic' : 'normal'
        });

        if (note) {
            note.setItalic(italic);
            this.canvas!.requestRenderAll();
            fireObjectModified(this.canvas!, note);
        }

        this.stateHasChanged();
    };

    addStickyNote = () => {
        const canvas = this.canvas!;

        this.disableFreehandDrawing();
        const canvasTransform = this.canvasTransform;

        const newNotePositionX = canvas.width
            ? canvas.width / 2 / canvasTransform.scale -
              100 -
              canvasTransform.translationX / canvasTransform.scale
            : 50;
        const newNotePositionY = canvas.height
            ? canvas.height / 2 / canvasTransform.scale -
              100 -
              canvasTransform.translationY / canvasTransform.scale
            : 50;

        const note = new StickyNote({
            left: newNotePositionX,
            top: newNotePositionY,
            fill: this.stickyNoteDefaults.fill,
            fontSize: this.stickyNoteDefaults.fontSize,
            textAlign: this.stickyNoteDefaults.textAlign,
            fontFamily: this.stickyNoteDefaults.fontFamily,
            fontWeight: this.stickyNoteDefaults.fontWeight,
            fontStyle: this.stickyNoteDefaults.fontStyle
        });
        (note as unknown as CustomFabricObject).elementId = uuidv4();

        canvas.add(note);
        canvas.setActiveObject(note);
        this.setMovingCanvas(false);

        // Like Miro: a fresh note goes straight into text editing.
        note.enterTextEditing(canvas);
        this.stateHasChanged();
    };

    private resourceTargetFromFile = (
        file: FileDataModel
    ): ResourceCardTarget => ({
        kind: 'file',
        url: file.uri,
        fileName: file.fileName || file.name,
        extension: file.extension,
        byteLength: file.length
    });

    /*
     * Places a new resource card in the middle of the current viewport, the
     * same way addStickyNote does. Persistence needs no extra work here:
     * object:added is already in the event handler map.
     */
    private addResourceCard = (target: ResourceCardTarget) => {
        const canvas = this.canvas!;

        this.disableFreehandDrawing();
        const canvasTransform = this.canvasTransform;

        const newCardPositionX = canvas.width
            ? canvas.width / 2 / canvasTransform.scale -
              150 -
              canvasTransform.translationX / canvasTransform.scale
            : 50;
        const newCardPositionY = canvas.height
            ? canvas.height / 2 / canvasTransform.scale -
              44 -
              canvasTransform.translationY / canvasTransform.scale
            : 50;

        const card = new ResourceCard({
            left: newCardPositionX,
            top: newCardPositionY,
            ...target
        });
        (card as unknown as CustomFabricObject).elementId = uuidv4();

        canvas.add(card);
        canvas.setActiveObject(card);
        this.setMovingCanvas(false);
        this.stateHasChanged();

        return card;
    };

    addResourceLink = (url: string, title?: string) => {
        if (!isValidURL(url)) {
            return;
        }

        this.addResourceCard({kind: 'link', url, title});
    };

    addResourceFile = () => {
        this.openAssetSelectAndUploadModal(
            FileFormat.Any,
            'Please select a file to share',
            (file: FileDataModel | null) => {
                if (file) {
                    this.addResourceCard(this.resourceTargetFromFile(file));
                }
            }
        );
    };

    get activeResourceCard(): ResourceCard | null {
        const activeObject = this.canvas?.getActiveObject();
        return activeObject instanceof ResourceCard ? activeObject : null;
    }

    /*
     * Opens a link card's or a linked picture's url in a new tab.
     *
     * The url is re-validated here and not only where the link was set. It
     * arrives over SignalR from other collaborators and out of the stored
     * project data, so what was validated at creation says nothing about
     * what is actually being opened now. Without this check a peer could
     * store a javascript: url that would then run on the app's origin.
     */
    private openExternalLink(url: string) {
        if (!isValidURL(url)) {
            console.warn('Refusing to open a link with an unsafe url');
            return;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    }

    /*
     * Opens a card's target. A link opens in a new tab. A file goes through
     * the app's own download endpoint, which streams the blob with a
     * Content-Disposition of attachment so the browser saves it rather than
     * rendering it, and which is same-origin, so the anchor click downloads
     * without navigating away from the board. A file card's url is validated
     * for the same reason as a link's.
     */
    openResource = (card: ResourceCard) => {
        const url = card.url;

        if (card.kind !== 'file') {
            this.openExternalLink(url);
            return;
        }

        if (!isValidURL(url)) {
            console.warn('Refusing to open a resource card with an unsafe url');
            return;
        }

        const downloadUrl =
            baseUri + '/api/file/download?uri=' + encodeURIComponent(url);

        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = '';
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    };

    openActiveResourceCard = () => {
        const card = this.activeResourceCard;
        if (card) {
            this.openResource(card);
        }
    };

    editResourceCardTitle = () => {
        const card = this.activeResourceCard;
        if (card) {
            card.enterTitleEditing(this.canvas!);
        }
    };

    setResourceCardColor = (color: string) => {
        const card = this.activeResourceCard;
        if (!card) {
            return;
        }

        card.setColor(color);
        this.canvas!.requestRenderAll();
        fireObjectModified(this.canvas!, card);
        this.stateHasChanged();
    };

    replaceResourceCardFile = () => {
        const card = this.activeResourceCard;
        if (!card) {
            return;
        }

        this.openAssetSelectAndUploadModal(
            FileFormat.Any,
            'Please select a file to share',
            (file: FileDataModel | null) => {
                if (file) {
                    card.setTarget(this.resourceTargetFromFile(file));
                    this.canvas!.requestRenderAll();
                    fireObjectModified(this.canvas!, card);
                    this.stateHasChanged();
                }
            }
        );
    };

    setResourceCardLink = (url: string, title?: string) => {
        const card = this.activeResourceCard;
        if (!card || !isValidURL(url)) {
            return;
        }

        card.setTarget({kind: 'link', url, title});
        this.canvas!.requestRenderAll();
        fireObjectModified(this.canvas!, card);
        this.stateHasChanged();
    };

    get activeLinkableImage(): LinkableImage | null {
        const activeObject = this.canvas?.getActiveObject();
        return activeObject instanceof LinkableImage ? activeObject : null;
    }

    private setActiveImageLink(link: string | null) {
        const image = this.activeLinkableImage;
        if (!image) {
            return;
        }

        image.link = link;
        this.canvas!.requestRenderAll();
        fireObjectModified(this.canvas!, image);
        this.stateHasChanged();
    }

    setImageLink = (url: string) => {
        if (isValidURL(url)) {
            this.setActiveImageLink(url);
        }
    };

    // Kept as null rather than deleted so the removal is persisted.
    removeImageLink = () => {
        this.setActiveImageLink(null);
    };

    openActiveImageLink = () => {
        const link = this.activeLinkableImage?.link;
        if (link) {
            this.openExternalLink(link);
        }
    };

    addText = (textToInsert?: string) => {
        const canvas = this.canvas!;

        this.disableFreehandDrawing();
        const canvasTransform = this.canvasTransform;

        const newTextPositionX = canvas.width
            ? canvas.width / 2 / canvasTransform.scale -
              50 -
              canvasTransform.translationX / canvasTransform.scale
            : 50;
        const newTextPositionY = canvas.height
            ? canvas.height / 2 / canvasTransform.scale -
              10 -
              canvasTransform.translationY / canvasTransform.scale
            : 50;
        const elementId = uuidv4();
        const text = new fabric.IText(textToInsert ?? 'Insert Text', {
            left: newTextPositionX,
            top: newTextPositionY,
            fontFamily: this.fontSettings.font ?? 'Arial',
            fill: this.currentColor,
            fontSize: this.fontSettings.fontSize ?? 20
        }) as unknown as CustomFabricObject;
        text.elementId = elementId;

        canvas.add(text);
        canvas.setActiveObject(text);
        this.setMovingCanvas(false);
    };

    private addImageFromUrl = (url: string) => {
        const canvas = this.canvas!;
        const canvasTransform = this.canvasTransform;
        LinkableImage.fromURL(url, {
            crossOrigin: 'anonymous' // Handle CORS if loading external images
        })
            .then(img => {
                const newImagePositionX = canvas.width
                    ? canvas.width / 2 / canvasTransform.scale -
                      50 -
                      canvasTransform.translationX / canvasTransform.scale
                    : 50;
                const newImagePositionY = canvas.height
                    ? canvas.height / 2 / canvasTransform.scale -
                      50 -
                      canvasTransform.translationY / canvasTransform.scale
                    : 50;
                img.set({
                    left: newImagePositionX,
                    top: newImagePositionY,
                    angle: 0,
                    cornerSize: 10
                });
                img.scaleToWidth(100);
                img.scaleToHeight(100);

                const elementId = uuidv4();
                (img as unknown as CustomFabricObject).elementId = elementId;
                canvas.add(img);

                // Manually fire the EventTypes.ObjectModified event
                fireObjectModified(canvas, img);
            })
            .catch(error => {
                console.error('Failed to add image', error);
            });
    };

    addImage = () => {
        this.disableFreehandDrawing();
        this.openAssetSelectAndUploadModal(
            FileFormat.Image,
            'Please select a picture file',
            (file: FileDataModel | null) => {
                if (file) {
                    this.addImageFromUrl(file.uri);
                }
            }
        );
        this.setMovingCanvas(false);
    };

    addNewVideo = (
        src: string,
        videoToClone?: CustomFabricObject,
        activeObject?: fabric.FabricObject,
        pointOnCanvas?: fabric.Point
    ) => {
        const canvasTransform = this.canvasTransform;
        const videoElement: HTMLVideoElement = document.createElement('video');
        videoElement.style.display = 'none';
        videoElement.src = src;
        videoElement.setAttribute('crossOrigin', 'anonymous');
        videoElement.load();
        videoElement.onloadeddata = () => {
            const elementId = uuidv4();

            // Update the state with the new video element and its associated id
            this.videoElements = [
                ...this.videoElements,
                {id: elementId, element: videoElement}
            ];
            videoElement.width = videoElement.videoWidth;
            videoElement.height = videoElement.videoHeight;
            let newVideoPositionX;
            let newVideoPositionY;
            let scaleX;
            let scaleY;
            let angle;

            if (videoToClone) {
                if (activeObject) {
                    newVideoPositionX = pointOnCanvas!.x + 10;
                    newVideoPositionY = pointOnCanvas!.y + 10;
                    scaleX = activeObject.scaleX * videoToClone.scaleX!;
                    scaleY = activeObject.scaleY * videoToClone.scaleY!;
                    angle = activeObject.angle + videoToClone.angle!;
                } else {
                    newVideoPositionX = videoToClone.left! + 10;
                    newVideoPositionY = videoToClone.top! + 10;
                    scaleX = videoToClone.scaleX;
                    scaleY = videoToClone.scaleY;
                    angle = videoToClone.angle;
                }
            } else {
                scaleX = 1;
                scaleY = 1;
                angle = 0;

                if (videoElement.width > this.canvas!.width! / 2) {
                    scaleX = this.canvas!.width! / 2 / videoElement.width;
                    scaleY = scaleX;
                }

                newVideoPositionX = this.canvas!.width
                    ? this.canvas!.width / 2 / canvasTransform.scale -
                      (videoElement.width * scaleX) / 2 -
                      canvasTransform.translationX / canvasTransform.scale
                    : 50;
                newVideoPositionY = this.canvas!.height
                    ? this.canvas!.height / 2 / canvasTransform.scale -
                      (videoElement.height * scaleY) / 2 -
                      canvasTransform.translationY / canvasTransform.scale
                    : 50;
            }

            const video = new FabricVideo(videoElement, {
                left: newVideoPositionX,
                top: newVideoPositionY,
                scaleX: scaleX,
                scaleY: scaleY,
                angle: angle,
                objectCaching: false
            });

            // Casting to CustomFabricObject and setting properties
            const customVideo: CustomFabricObject =
                video as unknown as CustomFabricObject;
            customVideo.elementId = elementId;
            customVideo.uri = src;
            customVideo.videoElement = videoElement;

            this.canvas!.add(customVideo);
            //this.canvas!.setActiveObject(customVideo);
            fireObjectModified(this.canvas!, video);
            customVideo.videoElement.play();

            // For rendering the video
            const c = this.canvas!;
            requestAnimationFrame(function render() {
                c.renderAll();
                requestAnimationFrame(render);
            });
        };
    };

    addVideo = () => {
        this.disableFreehandDrawing();

        this.openAssetSelectAndUploadModal(
            FileFormat.Video,
            'Please select a video file (mp4)',
            (file: FileDataModel | null) => {
                if (file) {
                    this.addNewVideo(file.uri);
                }
            }
        );
        this.setMovingCanvas(false);
    };

    playSelectedVideo = () => {
        const activeObject: fabric.FabricObject | undefined =
            this.canvas!.getActiveObject();
        if (activeObject) {
            const activeElementId: string | undefined = (
                activeObject as CustomFabricObject
            ).elementId;

            this.videoElements.forEach((videoElement: VideoElement): void => {
                if (videoElement.id === activeElementId) {
                    videoElement.element.play();
                }
            });
        }
    };

    pauseSelectedVideo = () => {
        const activeObject: fabric.FabricObject | undefined =
            this.canvas!.getActiveObject();
        if (activeObject) {
            const activeElementId: string | undefined = (
                activeObject as CustomFabricObject
            ).elementId;
            this.videoElements.forEach((videoElement: VideoElement): void => {
                if (videoElement.id === activeElementId) {
                    videoElement.element.pause();
                }
            });
        }
    };

    private updateCanvasSize = () => {
        /*
        const dimensions = {
            width: window.innerWidth - 50,
            height: window.innerHeight - 160
        };
        */

        const canvasContainer = document.getElementById('CanvasContainer');
        if (canvasContainer) {
            const {width} = canvasContainer.getBoundingClientRect();
            const isFocusMode = canvasContainer.closest(
                '.focus-mode-surface--active'
            );
            const dimensions = {
                width: width,
                height: isFocusMode
                    ? window.innerHeight
                    : window.innerHeight - 160
            };

            this.canvas!.setDimensions(dimensions);
        }
    };

    initialize = (
        canvas: CustomCanvas,
        getCurrentProjectData: () => EntityDataModel,
        manipulation: EntityManipulationMethods,
        _playerEvent: (player: PlayerEvent) => void
    ) => {
        if (this.initialized) {
            return;
        }

        this.canvas = canvas;

        this.getCurrentProjectData = getCurrentProjectData;
        this.manipulation = manipulation;
        // this.playerEvent = playerEvent;

        this.updateCanvasSize();

        window.addEventListener('resize', this.updateCanvasSize);

        Object.keys(this.eventHandlers).forEach(eventType => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.canvas!.on(eventType as any, this.eventHandlers[eventType]);
        });

        this.canvas.selection = !this.isReadOnly;
        this.applyGrid();

        if (this.initialData) {
            this.restoreFabricObjects(this.initialData);
            this.initialDataObjectsRestored = true;
            this.initialData = null;
        }

        if (this.isReadOnly) {
            this.setMovingCanvas(true);
        }

        this.initialized = true;
        this.stateHasChanged();
    };

    dispose = () => {
        this.initialized = false;
        this.history.flush();

        window.removeEventListener('resize', this.updateCanvasSize);

        if (this.canvas && this.eventHandlers) {
            Object.keys(this.eventHandlers).forEach(eventType => {
                this.canvas!.off(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    eventType as any,
                    this.eventHandlers[eventType]
                );
            });
        }

        this.detachCanvasNavigation();
        this.canvas && this.canvas.dispose();
        this.canvas = null;
        this.initialData = null;
        this.initialDataObjectsRestored = false;
    };

    onRemoteMergeData: MergeData = (
        elementId: string,
        data: UnknownObject,
        meta?: string | null
    ) => {
        console.log(`onRemoteMergeData elementId=${elementId}, meta=${meta}`);

        // First, before the canvas: the add this triggers must find the id already known.
        this.history.applyRemote(elementId, data);

        const applyBatchedChanges = () => {
            Object.entries(data).forEach(
                ([entryElementId, entryElementData]) => {
                    this.onRemoteMergeElement(
                        entryElementId,
                        entryElementData as UnknownObject,
                        meta
                    );
                }
            );
        };

        if (elementId === emptyGuid) {
            if (meta === EventTypes.OrdinalPositionsChange) {
                const canvas = this.canvas!;
                const objects = this.getObjects();

                // Create a map of elementId to its new ordinalPosition
                const ordinalPositionMap = new Map<string, number>();
                Object.entries(data).forEach(([elementId, elementData]) => {
                    ordinalPositionMap.set(
                        elementId,
                        (elementData as CustomFabricObject).ordinalPosition ?? 0
                    );
                });

                // Update the ordinalPosition of each object on the canvas
                objects.forEach(obj => {
                    const elementObject = obj as CustomFabricObject;
                    const newOrdinalPosition = ordinalPositionMap.get(
                        elementObject.elementId ?? ''
                    );
                    if (newOrdinalPosition !== undefined) {
                        elementObject.ordinalPosition = newOrdinalPosition;
                    }
                });

                // Sort the objects based on their updated ordinalPosition
                objects.sort((a, b) => {
                    const elementA = a as CustomFabricObject;
                    const elementB = b as CustomFabricObject;
                    return (
                        (elementA.ordinalPosition ?? 0) -
                        (elementB.ordinalPosition ?? 0)
                    );
                });

                // Reorder the objects on the canvas based on the sorted order
                objects.forEach((obj, index) => {
                    const currentIndex = canvas.getObjects().indexOf(obj);
                    const desiredIndex = index;

                    if (currentIndex !== desiredIndex) {
                        const steps = currentIndex - desiredIndex;
                        for (let i = 0; i < steps; i++) {
                            canvas.sendObjectBackwards(obj);
                        }
                    }
                });

                // Refresh the canvas to reflect the changes
                canvas.requestRenderAll();
            }

            applyBatchedChanges();
        } else {
            this.onRemoteMergeElement(elementId, data, meta);
        }
    };

    /*
        onImageEdited={(videoElements) =>
            typeof videoElements === 'function'
                ? this.videoElements=(videoElements(his.videoElements))
                : this.videoElements=(videoElements)
        }
    * */

    private onRemoteMergeElement: MergeData = (
        elementId: string,
        data: UnknownObject,
        meta?: string | null
    ) => {
        if (meta === EventTypes.ObjectAdded) {
            restoreFabricObject(
                this.canvas!,
                elementId,
                data,
                videoElements =>
                    typeof videoElements === 'function'
                        ? (this.videoElements = videoElements(
                              this.videoElements
                          ))
                        : (this.videoElements = videoElements),
                this.isReadOnly
            );
        } else if (meta?.startsWith('object:')) {
            const objectToModify = findFabricObject(this.canvas!, elementId);

            if (objectToModify) {
                modifyFabricObject(
                    this.canvas!,
                    objectToModify,
                    elementId,
                    data
                );
                // A collaborator may have just linked or unlinked a picture.
                if (this.isReadOnly) {
                    this.applyReadOnlyClickability(objectToModify);
                }
                this.canvas!.requestRenderAll();
            } else {
                console.error(`Object with elementId ${elementId} not found.`);
            }
        }
    };

    onRemoteRemoveElement: RemoveElement = (
        elementId: string,
        meta?: string | null
    ) => {
        console.log(
            `onRemoteRemoveElement elementId=${elementId}, meta=${meta}`
        );

        this.history.applyRemoteRemove(elementId);

        const objectToRemove = findFabricObject(this.canvas!, elementId);

        if (objectToRemove) {
            this.canvas!.remove(objectToRemove);
            this.canvas!.requestRenderAll();
        } else {
            console.error(`Object with elementId ${elementId} not found.`);
        }
    };

    private debouncedCreateSprite = debounce(
        (playerId: string, x: number, y: number) => {
            const player: Player | undefined = this.players[playerId];

            if (!player) {
                return;
            }

            Sprite.fromSpriteURL(
                `${this.storageUri}${player.containerName}/profile/sprite.png`
            )
                .then(this.createSprite(playerId, x ?? 0, y ?? 0))
                .catch(error => {
                    console.error('Failed to load player sprite', error);
                });
        },
        250
    );

    onRemotePlayerEvent = (playerEvent: PlayerEvent) => {
        const playerId: string = playerEvent.userId;
        const playerSprite = findFabricObject(this.canvas!, playerId);

        // Clear any existing timer for this player using playerTimersRef
        if (this.playerTimersRef.current[playerId]) {
            clearTimeout(this.playerTimersRef.current[playerId]);
        }

        if (playerEvent.eventType === 'move') {
            // Player has moved, reset or create a new timer
            this.playerTimersRef.current[playerId] = setTimeout(() => {
                if (playerSprite) {
                    this.canvas!.remove(playerSprite);
                }
                delete this.playerTimersRef.current[playerId];
            }, 30000); // 30 seconds

            if (playerSprite) {
                playerSprite.set({
                    left: playerEvent.x,
                    top: playerEvent.y
                });
            } else {
                this.debouncedCreateSprite(
                    playerId,
                    playerEvent.x ?? 0,
                    playerEvent.y ?? 0
                );
            }

            if (!(playerId in this.players)) {
                this.players = {...this.players, [playerId]: playerEvent};
            }
        } else if (playerEvent.eventType === 'exit') {
            if (
                playerId in this.players &&
                this.playerTimersRef.current[playerId]
            ) {
                clearTimeout(this.playerTimersRef.current[playerId]);
                delete this.playerTimersRef.current[playerId];
                if (playerSprite) {
                    this.canvas!.remove(playerSprite);
                }

                delete this.players[playerId];
                this.players = {...this.players};
            }
        }

        this.canvas!.renderAll();
    };

    changeColor = (color: string) => {
        const activeObject = this.canvas!.getActiveObject();
        if (activeObject) {
            if (activeObject instanceof StickyNote) {
                activeObject.setColor(color);
            } else if (getObjectType(activeObject) === 'path') {
                activeObject.set('stroke', color);
            } else {
                activeObject.set('fill', color);
            }
            this.canvas!.requestRenderAll();
            // Manually fire the 'object:modified' event
            fireObjectModified(this.canvas!, activeObject);
        }

        // handle color change for all objects in a group
        const activeGroup = this.canvas!.getActiveObjects();
        if (activeGroup) {
            activeGroup.forEach(object => {
                if (object instanceof StickyNote) {
                    object.setColor(color);
                } else if (getObjectType(object) === 'path') {
                    object.set('stroke', color);
                } else {
                    object.set('fill', color);
                }
                this.canvas!.requestRenderAll();
                // Manually fire the 'object:modified' event
                fireObjectModified(this.canvas!, object);
            });
        }
    };

    handleColorChange = (color: string) => {
        this.currentColor = color;
        this.changeColor(color);
        if (this.canvas!.freeDrawingBrush) {
            // make sure the pencil tool gets the new color
            this.canvas!.freeDrawingBrush.color = color;
        }
        this.stateHasChanged();
    };

    onFontChange = (font: string | undefined) => {
        const canvas = this.canvas!;
        const activeObject = canvas.getActiveObject();
        if (activeObject instanceof fabric.IText) {
            const thisText: fabric.IText = activeObject;

            const fontSettings = this.fontSettings;
            this.fontSettings = {
                ...fontSettings,
                font: font
            };

            thisText.set('fontFamily', font);

            canvas.requestRenderAll();
            this.stateHasChanged();
        }
    };

    setFontOptions = (setting: string) => {
        const activeObject = this.canvas!.getActiveObject();
        if (!activeObject || getObjectType(activeObject) !== ObjectTypes.Text) {
            return;
        }

        const textObject = activeObject as fabric.IText;

        switch (setting) {
            case FontOptions.Italic:
                textObject.fontStyle =
                    textObject.fontStyle === 'italic' ? 'normal' : 'italic';
                this.fontSettings = {
                    ...this.fontSettings,
                    italic: !this.fontSettings.italic
                };
                break;
            case FontOptions.Bold:
                textObject.fontWeight =
                    textObject.fontWeight === FontOptions.Bold
                        ? ''
                        : FontOptions.Bold;
                this.fontSettings = {
                    ...this.fontSettings,
                    bold: !this.fontSettings.bold
                };
                break;
            default:
                break;
        }

        fireObjectModified(this.canvas!, textObject);
        this.canvas!.renderAll();
        this.stateHasChanged();
    };

    setTextAlign = (align: TextAlign) => {
        const activeObject = this.canvas!.getActiveObject();
        if (!activeObject || getObjectType(activeObject) !== ObjectTypes.Text) {
            return;
        }

        const textObject = activeObject as fabric.IText;
        textObject.set('textAlign', align);
        this.fontSettings = {...this.fontSettings, align};

        fireObjectModified(this.canvas!, textObject);
        this.canvas!.renderAll();
        this.stateHasChanged();
    };

    // Moves the selection by a step (the arrow keys), or to the next grid line when snapping.
    nudgeSelected = (dx: number, dy: number) => {
        const canvas = this.canvas;
        const activeObject = canvas?.getActiveObject();
        if (!canvas || !activeObject || this.isReadOnly) {
            return;
        }

        if (this.grid.snap) {
            const size = this.grid.size;
            activeObject.set({
                left: snapToGrid(
                    activeObject.left + Math.sign(dx) * size,
                    size
                ),
                top: snapToGrid(activeObject.top + Math.sign(dy) * size, size)
            });
        } else {
            activeObject.set({
                left: activeObject.left + dx,
                top: activeObject.top + dy
            });
        }

        activeObject.setCoords();
        canvas.requestRenderAll();
        fireObjectModified(canvas, activeObject);
    };

    deselect = () => {
        const canvas = this.canvas;
        if (!canvas) {
            return;
        }
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        this.stateHasChanged();
    };

    selectAll = () => {
        const canvas = this.canvas;
        if (!canvas || this.isReadOnly) {
            return;
        }

        const objects = canvas.getObjects().filter(obj => obj.selectable);
        if (objects.length === 0) {
            return;
        }

        canvas.discardActiveObject();
        canvas.setActiveObject(
            objects.length === 1
                ? objects[0]
                : new fabric.ActiveSelection(objects, {canvas})
        );
        canvas.requestRenderAll();
        this.stateHasChanged();
    };

    setGrid = (changes: Partial<GridSettings>) => {
        this.grid = {...this.grid, ...changes};
        saveGridSettings(this.grid);
        this.applyGrid();
        this.stateHasChanged();
    };

    private applyGrid = () => {
        const canvas = this.canvas;
        if (!canvas) {
            return;
        }

        if (this.grid.show) {
            if (!this.gridBackground) {
                this.gridBackground = new GridBackground(
                    canvas,
                    this.grid.size
                );
            }
            this.gridBackground.setSize(this.grid.size);
            canvas.backgroundImage = this.gridBackground;
        } else if (canvas.backgroundImage === this.gridBackground) {
            canvas.backgroundImage = undefined;
        }

        canvas.requestRenderAll();
    };

    private snapToGrid = (target: fabric.FabricObject | undefined) => {
        if (!target || !this.grid.snap) {
            return;
        }

        const size = this.grid.size;
        target.set({
            left: snapToGrid(target.left, size),
            top: snapToGrid(target.top, size)
        });
    };

    setFontSize = (fontSize: number | undefined) => {
        const activeObject = this.canvas!.getActiveObject();
        if (!activeObject || getObjectType(activeObject) !== ObjectTypes.Text) {
            return;
        }

        const textObject = activeObject as fabric.IText;

        textObject.set('fontSize', fontSize);
        this.fontSettings = {...this.fontSettings, fontSize: fontSize};

        fireObjectModified(this.canvas!, textObject);
        this.canvas!.renderAll();
        this.stateHasChanged();
    };

    onLayersChanged = () => {
        const batchedChanges: Record<string, {ordinalPosition: number}> = {};

        const canvasObjects = this.canvas!.getObjects();

        canvasObjects.forEach((obj, index) => {
            const object = obj as CustomFabricObject;
            const elementId = object.elementId;
            if (elementId) {
                // The history's mirror is never a render behind, unlike project data.
                const stored =
                    this.history.get(elementId)?.ordinalPosition ??
                    object.ordinalPosition;

                if (stored !== index) {
                    object.ordinalPosition = index;
                    batchedChanges[elementId] = {ordinalPosition: index};
                }
            }
        });

        if (Object.keys(batchedChanges).length === 0) {
            return;
        }

        this.history.recordOrder(batchedChanges);
        this.mergeData(
            emptyGuid,
            batchedChanges,
            EventTypes.OrdinalPositionsChange
        );
    };

    bringForward = () => {
        const activeObject = this.canvas!.getActiveObject();
        if (activeObject) {
            this.canvas!.bringObjectForward(activeObject);
            this.canvas!.discardActiveObject();
            this.canvas!.requestRenderAll();
            this.onLayersChanged();
        }
    };

    bringBackwards = () => {
        const activeObject = this.canvas!.getActiveObject();

        if (activeObject) {
            this.canvas!.sendObjectBackwards(activeObject);
            this.canvas!.discardActiveObject();
            this.canvas!.requestRenderAll();
            this.onLayersChanged();
        }
    };

    bringToFront = () => {
        const activeObject = this.canvas!.getActiveObject();
        if (activeObject) {
            this.canvas!.bringObjectToFront(activeObject);
            this.canvas!.discardActiveObject();
            this.canvas!.requestRenderAll();
            this.onLayersChanged();
        }
    };

    sendToBack = () => {
        const activeObject = this.canvas!.getActiveObject();
        if (activeObject) {
            this.canvas!.sendObjectToBack(activeObject);
            this.canvas!.discardActiveObject();
            this.canvas!.requestRenderAll();
            this.onLayersChanged();
        }
    };

    groupItems = () => {
        if (!this.canvas) {
            return;
        }

        const canvas = this.canvas;
        const activeObject = canvas.getActiveObject();

        if (activeObject instanceof fabric.ActiveSelection) {
            /*
             * v6+ removed ActiveSelection.toGroup; the equivalent is to pull
             * the objects out of the selection (which restores their canvas
             * coordinates), replace them on the canvas with a new group.
             */
            const objects = activeObject.removeAll();
            canvas.remove(...objects);
            const group = new fabric.Group(objects);
            canvas.add(group);
            canvas.setActiveObject(group);
            canvas.requestRenderAll();
        }
    };

    ungroupItems = () => {
        if (!this.canvas) {
            return;
        }

        const canvas = this.canvas;
        const activeGroup = canvas.getActiveObject();

        if (
            activeGroup instanceof fabric.Group &&
            !(activeGroup instanceof fabric.ActiveSelection)
        ) {
            const canvasObjects = canvas.getObjects();
            const indexOfActiveGroup = canvasObjects.indexOf(activeGroup);

            canvas.remove(activeGroup);

            /*
             * v6+ group removal restores the children's canvas-plane
             * transforms, replacing the manual matrix math this used to need.
             */
            const ungroupedObjects = activeGroup.removeAll();
            canvas.add(...ungroupedObjects);

            ungroupedObjects.forEach((object, index) => {
                canvas.moveObjectTo(object, indexOfActiveGroup + index);
            });

            const newSelection = new fabric.ActiveSelection(ungroupedObjects, {
                canvas: canvas
            });

            canvas.setActiveObject(newSelection);
            canvas.requestRenderAll();

            this.onLayersChanged();
        }
    };

    /*
     * Bounding box of an object in its selection's coordinate plane
     * (children's left/top are relative to the selection centre), so rotated
     * objects align/distribute by their true bounding box.
     */
    private selectionPlaneBounds = (obj: fabric.FabricObject) => {
        const matrix = obj.calcOwnMatrix();
        const corners = [
            transformPoint({x: -obj.width / 2, y: -obj.height / 2}, matrix),
            transformPoint({x: obj.width / 2, y: -obj.height / 2}, matrix),
            transformPoint({x: obj.width / 2, y: obj.height / 2}, matrix),
            transformPoint({x: -obj.width / 2, y: obj.height / 2}, matrix)
        ];
        const xs = corners.map(point => point.x);
        const ys = corners.map(point => point.y);
        return {
            left: Math.min(...xs),
            right: Math.max(...xs),
            top: Math.min(...ys),
            bottom: Math.max(...ys)
        };
    };

    /*
     * PowerPoint-style alignment of a multi-selection: every selected object
     * is aligned to the selection's bounding box.
     */
    alignSelectedItems = (alignment: ObjectAlignment) => {
        const canvas = this.canvas;
        if (!canvas) {
            return;
        }

        const selection = canvas.getActiveObject();
        if (!(selection instanceof fabric.ActiveSelection)) {
            return;
        }

        const objects = selection.getObjects();
        if (objects.length < 2) {
            return;
        }

        const halfWidth = selection.width / 2;
        const halfHeight = selection.height / 2;

        objects.forEach(obj => {
            const bounds = this.selectionPlaneBounds(obj);

            let deltaX = 0;
            let deltaY = 0;
            switch (alignment) {
                case 'left':
                    deltaX = -halfWidth - bounds.left;
                    break;
                case 'centerX':
                    deltaX = -(bounds.left + bounds.right) / 2;
                    break;
                case 'right':
                    deltaX = halfWidth - bounds.right;
                    break;
                case 'top':
                    deltaY = -halfHeight - bounds.top;
                    break;
                case 'centerY':
                    deltaY = -(bounds.top + bounds.bottom) / 2;
                    break;
                case 'bottom':
                    deltaY = halfHeight - bounds.bottom;
                    break;
            }

            if (deltaX !== 0 || deltaY !== 0) {
                obj.set({left: obj.left + deltaX, top: obj.top + deltaY});
                obj.setCoords();
            }
        });

        selection.dirty = true;
        canvas.requestRenderAll();

        // Persist every child's new scene position (same pipeline as
        // dragging a multi-selection).
        this.activeSelectionEvent(EventTypes.ObjectModified, selection);
        this.stateHasChanged();
    };

    /*
     * PowerPoint-style distribution: the outermost objects stay where they
     * are and the gaps between the objects in between are equalised.
     */
    distributeSelectedItems = (direction: DistributeDirection) => {
        const canvas = this.canvas;
        if (!canvas) {
            return;
        }

        const selection = canvas.getActiveObject();
        if (!(selection instanceof fabric.ActiveSelection)) {
            return;
        }

        const objects = selection.getObjects();
        if (objects.length < 3) {
            return;
        }

        const horizontal = direction === 'horizontal';
        const start = (bounds: {left: number; top: number}) =>
            horizontal ? bounds.left : bounds.top;
        const end = (bounds: {right: number; bottom: number}) =>
            horizontal ? bounds.right : bounds.bottom;

        const entries = objects
            .map(obj => ({obj, bounds: this.selectionPlaneBounds(obj)}))
            .sort(
                (a, b) =>
                    start(a.bounds) +
                    end(a.bounds) -
                    (start(b.bounds) + end(b.bounds))
            );

        const spanStart = start(entries[0].bounds);
        const spanEnd = end(entries[entries.length - 1].bounds);
        const totalSize = entries.reduce(
            (sum, entry) => sum + (end(entry.bounds) - start(entry.bounds)),
            0
        );
        const gap = (spanEnd - spanStart - totalSize) / (entries.length - 1);

        let cursor = spanStart;
        entries.forEach(entry => {
            const size = end(entry.bounds) - start(entry.bounds);
            const delta = cursor - start(entry.bounds);

            if (delta !== 0) {
                if (horizontal) {
                    entry.obj.set({left: entry.obj.left + delta});
                } else {
                    entry.obj.set({top: entry.obj.top + delta});
                }
                entry.obj.setCoords();
            }

            cursor += size + gap;
        });

        selection.dirty = true;
        canvas.requestRenderAll();

        this.activeSelectionEvent(EventTypes.ObjectModified, selection);
        this.stateHasChanged();
    };

    duplicateObject = () => {
        const activeObject = this.canvas!.getActiveObject();
        if (!activeObject) {
            return;
        }

        if (activeObject instanceof fabric.ActiveSelection) {
            const activeObjects = activeObject.getObjects();
            const clones: Promise<
                [fabric.FabricObject, fabric.FabricObject]
            >[] = [];

            activeObjects.forEach((obj: fabric.FabricObject) => {
                const object = obj as CustomFabricObject;
                const geometry = absoluteGeometry(obj, activeObject);

                if (object.videoElement) {
                    this.addNewVideo(
                        object.videoElement.src,
                        object,
                        activeObject,
                        new fabric.Point(geometry.left, geometry.top)
                    );
                    this.canvas!.requestRenderAll();
                } else {
                    clones.push(
                        object.clone().then((clone: fabric.FabricObject) => {
                            clone.set({
                                ...geometry,
                                left: geometry.left + DUPLICATE_OFFSET,
                                top: geometry.top + DUPLICATE_OFFSET
                            });
                            return [clone, object];
                        })
                    );
                }
            });

            // Added together once every clone is ready, so one Ctrl+D is one undo step.
            this.history.beginBatch();
            Promise.all(clones)
                .then(ready => {
                    ready.forEach(([clone, original]) =>
                        this.addDuplicate(clone, original)
                    );
                })
                .finally(() => this.history.endBatch());
        } else {
            const object = activeObject as CustomFabricObject;

            if (object.videoElement) {
                this.addNewVideo(object.videoElement.src, object);
                this.canvas!.requestRenderAll();
            } else {
                activeObject.clone().then((clone: fabric.FabricObject) => {
                    const left = activeObject.left || 0;
                    const top = activeObject.top || 0;
                    clone.set({
                        left: left + DUPLICATE_OFFSET,
                        top: top + DUPLICATE_OFFSET
                    });
                    this.addDuplicate(clone, object);
                    this.canvas!.setActiveObject(clone);
                });
            }
        }
    };

    /*
     * A copy lands just above its original, offset a little so both show,
     * rather than at the front of the whole board. Its position is set
     * before it is added, halfway between the original's and the next
     * object's (positions are numbers, not indices), so the add message
     * carries it and a reload keeps the copy where it landed; nothing else
     * needs renumbering.
     */
    private addDuplicate = (
        clone: fabric.FabricObject,
        original: fabric.FabricObject
    ) => {
        const canvas = this.canvas!;
        const objects = canvas.getObjects();
        const originalIndex = objects.indexOf(original);

        if (originalIndex >= 0) {
            const below = (original as CustomFabricObject).ordinalPosition;
            const above = (objects[originalIndex + 1] as CustomFabricObject)
                ?.ordinalPosition;

            if (typeof below === 'number') {
                (clone as CustomFabricObject).ordinalPosition =
                    typeof above === 'number' && above > below
                        ? (below + above) / 2
                        : below + 1;
            }
        }

        canvas.add(clone);

        if (originalIndex >= 0) {
            canvas.moveObjectTo(clone, originalIndex + 1);
        }

        canvas.requestRenderAll();
    };

    onInitialDataFetched = (initialData: EntityDataModel) => {
        console.log(`onInitialDataFetched initialized=${this.initialized}`);
        // Before anything reaches the canvas: a restore's object:added must find its id known.
        // Only for data the server just sent; the same snapshot again would wind the mirror back.
        if (initialData !== this.lastSeededData) {
            this.lastSeededData = initialData;
            this.history.seed(initialData);
        }
        if (this.initialized) {
            if (!this.initialDataObjectsRestored) {
                this.restoreFabricObjects(initialData);
                this.initialDataObjectsRestored = true;
            }
        } else {
            // We have the initial data before the canvas
            this.initialData = initialData;
        }
    };

    private mergeData = (
        elementId: string,
        data: UnknownObject,
        meta?: string | null
    ) => {
        this.manipulation.mergeData(elementId, data, meta);
    };

    private removeElement = (elementId: string, meta?: string | null) => {
        this.manipulation.removeElement(elementId, meta);
    };

    private restoreFabricObjects = (initialData: EntityDataModel) => {
        Object.entries(initialData)
            .sort(elementDataSorter)
            .forEach(([elementId, elementData]) => {
                const existingElement = findFabricObject(
                    this.canvas!,
                    elementId
                );
                if (!existingElement) {
                    restoreFabricObject(
                        this.canvas!,
                        elementId,
                        elementData,
                        videoElements =>
                            typeof videoElements === 'function'
                                ? (this.videoElements = videoElements(
                                      this.videoElements
                                  ))
                                : (this.videoElements = videoElements),
                        this.isReadOnly
                    );
                }
            });
    };

    private activeSelectionEvent(
        eventType: string,
        selection: fabric.FabricObject
    ) {
        const selectedObjects = selection as fabric.ActiveSelection;

        selectedObjects.forEachObject(obj => {
            const object = obj as CustomFabricObject;

            const elementId = (() => {
                if (object.elementId) {
                    return object.elementId;
                }

                const newElementId = uuidv4();
                object.set({
                    elementId: newElementId,
                    ordinalPosition:
                        object.ordinalPosition ?? this.nextOrdinalPosition()
                });
                return newElementId;
            })();

            const changes: UnknownObject = absoluteGeometry(
                obj,
                selectedObjects
            );

            this.history.recordElement(elementId, eventType, changes, true);
            this.mergeData(elementId, changes, eventType);
        });
    }

    private handleObjectEvent = (eventType: string, e: FabricEvent) => {
        // Other people's cursors are drawn on the canvas but are never board content.
        if (e.target instanceof Sprite) {
            return;
        }

        if (e.target && e.target instanceof fabric.ActiveSelection) {
            this.activeSelectionEvent(eventType, e.target);
            return;
        }

        /*
         * Events can target objects nested inside a group (e.g. the textbox
         * of a sticky note firing object:modified after editing). Only the
         * top-level object is a persisted element, so walk up to it —
         * but not out of an ActiveSelection, which is not a real container.
         */
        let target = e.target;
        while (
            target?.group &&
            !(target.group instanceof fabric.ActiveSelection)
        ) {
            target = target.group;
        }

        const object = target as CustomFabricObject;
        if (object) {
            const currentProjectData = this.getCurrentProjectData();

            if (object.ordinalPosition === undefined) {
                object.set({
                    ordinalPosition: this.nextOrdinalPosition()
                });
            }

            const elementId = (() => {
                if (object.elementId) {
                    return object.elementId;
                }

                const newElementId = uuidv4();
                object.set({
                    elementId: newElementId,
                    ordinalPosition:
                        object.ordinalPosition ?? this.nextOrdinalPosition()
                });
                return newElementId;
            })();

            /*
             * The history's mirror says whose event this is. A remove of an
             * element it no longer holds, and an add of one it already
             * holds, were done by a remote change, the initial restore or an
             * undo (the pictures and cards among those land on the canvas
             * asynchronously, which is why no flag could tell): already
             * broadcast by whoever did them, so not again from here.
             */
            if (eventType === EventTypes.ObjectRemoved) {
                if (!this.history.has(elementId)) {
                    return;
                }
                this.history.recordElement(elementId, eventType, null);
                this.removeElement(elementId, eventType);
                return;
            }

            if (
                eventType === EventTypes.ObjectAdded &&
                this.history.has(elementId)
            ) {
                return;
            }

            if (eventType === EventTypes.ObjectModified) {
                this.bakeTextScale(object);
            }

            const currentState = {
                ...normalizeSerializedElement(
                    object.toObject() as UnknownObject
                ),
                // Inside a selection an object's own geometry is relative to the selection's centre.
                ...(object.group instanceof fabric.ActiveSelection
                    ? absoluteGeometry(object, object.group)
                    : {}),
                elementId: object.elementId,
                ordinalPosition: object.ordinalPosition,
                meta: object.meta,
                nav: object.nav
            } as UnknownObject;

            const previousState =
                elementId in currentProjectData
                    ? currentProjectData[elementId]
                    : undefined;

            let changes: UnknownObject;
            if (previousState) {
                changes = getChanges(previousState, currentState);
            } else {
                changes = currentState;
            }

            this.history.recordElement(elementId, eventType, currentState);
            this.mergeData(elementId, changes, eventType);
        }
    };

    // --- Undo and redo ----------------------------------------------------

    undo = () => {
        if (!this.readyForHistory()) {
            return;
        }
        this.finishTextEditing();
        this.applyHistory(this.history.undo());
    };

    redo = () => {
        if (!this.readyForHistory()) {
            return;
        }
        this.finishTextEditing();
        this.applyHistory(this.history.redo());
    };

    // Not while reading, and not in the middle of a drag.
    private readyForHistory = (): boolean =>
        this.canvas !== null &&
        !this.isReadOnly &&
        !(this.canvas as unknown as {_currentTransform?: unknown})
            ._currentTransform;

    // Typing is committed first, so what is undone is what is on the board.
    private finishTextEditing = () => {
        this.canvas?.getObjects().forEach(obj => {
            const texts =
                obj instanceof fabric.Group ? obj.getObjects() : [obj];
            texts.forEach(text => {
                if (text instanceof fabric.IText && text.isEditing) {
                    text.exitEditing();
                }
            });
        });
    };

    /*
     * Carries out an undo or redo: each op goes onto the canvas the way a
     * remote change does (no canvas event reaches the server from here; the
     * history's mirror already knows these ids) and to the server the way
     * any edit does, so everyone else sees it as one more change.
     */
    private applyHistory = (ops: HistoryOp[]) => {
        const canvas = this.canvas;
        if (!canvas || ops.length === 0) {
            return;
        }

        canvas.discardActiveObject();

        this.history.suspend(() => {
            ops.forEach(op => {
                switch (op.kind) {
                    case 'add':
                        this.restoreElement(op.id, op.json);
                        this.mergeData(op.id, op.json, EventTypes.ObjectAdded);
                        break;

                    case 'remove': {
                        const object = findFabricObject(canvas, op.id);
                        if (object) {
                            this.disposeObject(object);
                            canvas.remove(object);
                        }
                        this.removeElement(op.id, EventTypes.ObjectRemoved);
                        break;
                    }

                    case 'modify': {
                        const object = findFabricObject(canvas, op.id);
                        if (object) {
                            modifyFabricObject(canvas, object, op.id, op.patch);
                            object.setCoords();
                        }
                        this.mergeData(
                            op.id,
                            op.patch,
                            EventTypes.ObjectModified
                        );
                        break;
                    }

                    case 'order':
                        this.onRemoteMergeData(
                            emptyGuid,
                            op.batch,
                            EventTypes.OrdinalPositionsChange
                        );
                        this.mergeData(
                            emptyGuid,
                            op.batch,
                            EventTypes.OrdinalPositionsChange
                        );
                        break;

                    case 'settings':
                        canvas.backgroundColor =
                            (op.json.canvasBackgroundColor as string) ?? '';
                        this.mergeData(op.id, op.json, EventTypes.ObjectAdded);
                        break;
                }
            });
        });

        canvas.requestRenderAll();
        this.stateHasChanged();
    };

    // Puts a stored element back on the canvas, pictures and videos included.
    private restoreElement = (elementId: string, json: ElementJson) => {
        restoreFabricObject(
            this.canvas!,
            elementId,
            json,
            videoElements =>
                typeof videoElements === 'function'
                    ? (this.videoElements = videoElements(this.videoElements))
                    : (this.videoElements = videoElements),
            this.isReadOnly
        );
    };

    // What a video needs before it leaves the canvas, so it stops playing and loading.
    private disposeObject = (object: fabric.FabricObject) => {
        const video = object as CustomFabricObject;
        if (object instanceof FabricVideo && video.videoElement) {
            video.videoElement.pause();
            video.videoElement.src = '';
            video.videoElement.load();
        }
    };

    /*
     * The position a new object takes: one past the highest on the board.
     * Its index in the list would fall behind after a delete (or an agent's
     * add, which counts the same way), and a position lower than something
     * already there puts the new object behind it on the next reload.
     */
    private nextOrdinalPosition = (): number => {
        let highest = -1;

        this.canvas?.getObjects().forEach(obj => {
            const ordinalPosition = (obj as CustomFabricObject).ordinalPosition;
            if (typeof ordinalPosition === 'number') {
                highest = Math.max(highest, ordinalPosition);
            }
        });

        Object.values(this.getCurrentProjectData()).forEach(element => {
            const ordinalPosition = element?.ordinalPosition;
            if (typeof ordinalPosition === 'number') {
                highest = Math.max(highest, ordinalPosition);
            }
        });

        return Math.floor(highest) + 1;
    };

    /*
     * Dragging a text's handles scales it, as it does any Fabric object.
     * Sticky notes and cards bake a resize into real properties once the
     * gesture ends; text does the same here, into its font size, so the size
     * shown in the toolbar (and read by the agent tools) is the size drawn,
     * and setting a size from either puts the text at that size.
     */
    private bakeTextScale = (object: CustomFabricObject) => {
        if (
            !(object instanceof fabric.IText) ||
            object.group ||
            (object.scaleX === 1 && object.scaleY === 1)
        ) {
            return;
        }

        const fontSize = Math.max(
            1,
            Math.round(object.fontSize * object.scaleY)
        );
        object.set({fontSize, scaleX: 1, scaleY: 1});
        object.setCoords();
        this.fontSettings = {...this.fontSettings, fontSize};
        this.canvas?.requestRenderAll();
    };

    private handleObjectMoving = (e: FabricEvent) => {
        this.snapToGrid(e.target);
        this.handleObjectEvent(EventTypes.ObjectMoving, e);
    };

    private handleObjectScaling = (e: FabricEvent) => {
        this.handleObjectEvent(EventTypes.ObjectScaling, e);
    };

    private handleObjectRotating = (e: FabricEvent) => {
        this.handleObjectEvent(EventTypes.ObjectRotating, e);
    };

    private handleObjectSkewing = (e: FabricEvent) => {
        this.handleObjectEvent(EventTypes.ObjectSkewing, e);
    };

    private handlePathCreated = (e: FabricEvent) => {
        this.handleObjectEvent(EventTypes.PathCreated, e);
        this.showPencilToolbar = false;
    };

    private handleObjectAdded = (e: FabricEvent) => {
        this.handleObjectEvent(EventTypes.ObjectAdded, e);
    };

    private handleObjectRemoved = (e: FabricEvent) => {
        this.handleObjectEvent(EventTypes.ObjectRemoved, e);
    };

    private handleObjectModified = (e: FabricEvent) => {
        this.handleObjectEvent(EventTypes.ObjectModified, e);
    };

    private handleMouseDown = (e: FabricEvent) => {
        if (!e.target) {
            return;
        }

        /*
         * Events on a nested child report the child as the target, so walk
         * out to the top-level object, as handleObjectEvent already does.
         */
        let target = e.target;
        while (
            target.group &&
            !(target.group instanceof fabric.ActiveSelection)
        ) {
            target = target.group;
        }

        /*
         * A resource card must be handled before the nav branch below: it
         * sets nav to its own (absolute, external) url purely to reuse the
         * read-only clickability plumbing, and that url must never reach
         * react-router's navigate.
         *
         * Only read-only mode activates on a single click. mouse:down fires
         * at the start of every drag, so activating in edit mode would make a
         * card impossible to reposition. Editors open a card by double-click
         * or from its toolbar instead.
         */
        if (target instanceof ResourceCard) {
            if (this.isReadOnly) {
                this.openResource(target);
            }
            return;
        }

        // Same single-click rule as a resource card.
        if (target instanceof LinkableImage && target.isLinkActive) {
            if (this.isReadOnly) {
                this.openExternalLink(target.link!);
            }
            return;
        }

        if (!this.isReadOnly) {
            return;
        }

        const customObject = target as CustomFabricObject;
        if (customObject.nav) {
            this.navigate(customObject.nav);
        }
    };

    private handleMouseDoubleClick = (e: FabricEvent) => {
        /*
         * Above the read-only guard on purpose: double-click is how an editor
         * tests a card without a single click hijacking every drag gesture.
         */
        if (e.target instanceof ResourceCard) {
            this.openResource(e.target);
            return;
        }

        if (e.target instanceof LinkableImage && e.target.isLinkActive) {
            this.openExternalLink(e.target.link!);
            return;
        }

        if (this.isReadOnly) {
            return;
        }

        if (e.target instanceof StickyNote) {
            e.target.enterTextEditing(this.canvas!);
        }
    };

    private handleMouseMove = (e: FabricEvent) => {
        if (!e.viewportPoint) {
            return;
        }

        const x = e.viewportPoint.x;
        const y = e.viewportPoint.y;

        this.playerEvent({
            eventType: 'move',
            userId: this.authData.id ?? emptyGuid,
            nickname: this.authData.nickname ?? '@anonymous',
            containerName: this.authData.container_name ?? 'anonymous',
            x,
            y
        });
    };

    private handleMouseOut = () => {
        this.playerEvent({
            eventType: 'exit',
            userId: this.authData.id ?? emptyGuid,
            nickname: this.authData.nickname ?? '@anonymous',
            containerName: this.authData.container_name ?? 'anonymous'
        });
    };

    private handleMouseUp = () => {
        this.stateHasChanged();
    };

    private handleSelectionCreated = (e: FabricEvent) => {
        console.log(EventTypes.SelectionCreated);
        this.setSelectedTextFontSettings(e);
        this.stateHasChanged();
    };

    private handleSelectionUpdated = (e: FabricEvent) => {
        console.log(EventTypes.SelectionUpdated);
        this.setSelectedTextFontSettings(e);
        this.stateHasChanged();
    };

    private handleSelectionCleared = () => {
        console.log(EventTypes.SelectionCleared);
        this.stateHasChanged();
    };

    private setSelectedTextFontSettings(e: FabricEvent) {
        const selectedObject = e.selected;

        if (selectedObject) {
            if (typeof selectedObject[0].fill === 'string') {
                this.currentColor = selectedObject[0].fill;
            }

            // Pick up the current state of the text object and set the font, bold, and italic states
            if (selectedObject[0] instanceof fabric.IText) {
                const activeObject = selectedObject[0] as fabric.IText;
                if (activeObject.fontFamily) {
                    this.fontSettings = {
                        ...this.fontSettings,
                        font: activeObject.fontFamily
                    };
                }

                if (activeObject.fontWeight) {
                    this.fontSettings = {
                        ...this.fontSettings,
                        bold: activeObject.fontWeight === FontOptions.Bold
                    };
                }

                if (activeObject.fontStyle) {
                    this.fontSettings = {
                        ...this.fontSettings,
                        italic: activeObject.fontStyle === FontOptions.Italic
                    };
                }
            }
        }
    }

    private handleTextEditingEntered = (e: FabricEvent) => {
        // A note's or card's text is nested; the element being edited is the top-level object.
        let target = e.target;
        while (
            target?.group &&
            !(target.group instanceof fabric.ActiveSelection)
        ) {
            target = target.group;
        }
        const textObject = target as CustomFabricObject | undefined;
        if (textObject) {
            this.editingTextElementId = textObject.elementId ?? null;
            this.stateHasChanged();
        }
    };

    private handleTextEditingExited = (e: FabricEvent) => {
        const textObject = e.target as CustomFabricObject;
        if (textObject) {
            this.editingTextElementId = null;
            this.stateHasChanged();
        }
    };

    captureCanvasImage = (): string => {
        if (!this.canvas) {
            console.error('Canvas is not initialized');
            return '';
        }

        // Get the current dimensions of the canvas
        const width = this.canvas.getWidth();
        const height = this.canvas.getHeight();

        // Create a temporary canvas with the same dimensions
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext('2d');

        if (!tempCtx) {
            console.error('Unable to get 2D context');
            return '';
        }

        // Fill the background if needed (e.g., white background)
        tempCtx.fillStyle = 'white';
        tempCtx.fillRect(0, 0, width, height);

        // Render the Fabric canvas onto the temporary canvas, without the grid: it is a guide, not content.
        const grid = this.canvas.backgroundImage;
        this.canvas.backgroundImage = undefined;
        this.canvas.renderAll();
        const fabricImage = this.canvas.toCanvasElement();
        this.canvas.backgroundImage = grid;
        this.canvas.requestRenderAll();
        tempCtx.drawImage(fabricImage, 0, 0);

        // Convert the temporary canvas to a data URI
        return tempCanvas.toDataURL('image/png');
    };

    createSprite = (playerId: string, i: number, j: number) => {
        return (sprite: Sprite) => {
            sprite.set({
                left: i,
                top: j,
                elementId: playerId,
                hasControls: false,
                hasBorders: false
            });
            this.canvas!.add(sprite);
            sprite.play();

            // not sure do we need the sprite animation play all the time, as
            // this approach will cause the canvas to be continuously redrawn,
            // which may impact performance

            // Animation loop to ensure continuous sprite animation
            const animate = () => {
                this.canvas!.renderAll();
                requestAnimationFrame(animate);
            };

            // Start the animation loop
            animate();
        };
    };
}

export default CanvasController;
